import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * 영어 발음 재생 (Web Speech API)
 *
 * 브라우저 내장 음성이라 서버 비용이 없다. 다만 음성 목록이 비동기로 로딩되고
 * 브라우저마다 지원이 달라, 쓸 수 있는 영어 음성이 없으면 버튼 자체를 숨겨야 한다.
 */
const useSpeech = () => {
  const [voice, setVoice] = useState(null);
  const [speaking, setSpeaking] = useState(null);
  const speakingRef = useRef(null);

  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return undefined;

    const pickVoice = () => {
      const voices = synth.getVoices();
      if (voices.length === 0) return;
      // 미국 영어를 우선하고, 없으면 아무 영어 음성이나 쓴다
      const en = voices.filter(v => /^en(-|_)/i.test(v.lang));
      setVoice(en.find(v => /US/i.test(v.lang)) || en[0] || null);
    };

    pickVoice();
    // 크롬은 목록이 늦게 채워져 voiceschanged 를 기다려야 한다
    synth.addEventListener('voiceschanged', pickVoice);
    return () => {
      synth.removeEventListener('voiceschanged', pickVoice);
      synth.cancel();
    };
  }, []);

  const speak = useCallback((text, key = text) => {
    const synth = window.speechSynthesis;
    if (!synth || !text) return;

    // 연달아 누르면 앞의 발음이 겹친다
    synth.cancel();

    const utter = new SpeechSynthesisUtterance(String(text));
    if (voice) {
      utter.voice = voice;
      utter.lang = voice.lang;
    } else {
      utter.lang = 'en-US';
    }
    // 학습용이라 조금 느리게
    utter.rate = 0.9;

    const done = () => {
      if (speakingRef.current === key) {
        speakingRef.current = null;
        setSpeaking(null);
      }
    };
    utter.onend = done;
    utter.onerror = done;

    speakingRef.current = key;
    setSpeaking(key);
    synth.speak(utter);
  }, [voice]);

  const stop = useCallback(() => {
    window.speechSynthesis?.cancel();
    speakingRef.current = null;
    setSpeaking(null);
  }, []);

  return {
    // 지원 여부를 알아야 버튼을 숨길지 정할 수 있다
    supported: typeof window !== 'undefined' && !!window.speechSynthesis && !!voice,
    speak,
    stop,
    speaking
  };
};

export default useSpeech;
